import { useEffect, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

// One shared poller for the whole app; pages subscribe with useLive(reload).
export const CLIENT_ID = Math.random().toString(36).slice(2) + Date.now().toString(36);
api.defaults.headers.common["X-Client-Id"] = CLIENT_ID;

const subs = new Set();
let timer = null;
let cursor = "";
let running = false;
let pending = null;

function notify() {
  clearTimeout(pending);
  pending = setTimeout(() => subs.forEach((fn) => { try { fn(); } catch (_e) { /* ignore */ } }), 250);
}

// Own changes: refresh instantly, no need to wait for the poll.
api.interceptors.response.use((res) => {
  const m = (res.config?.method || "get").toLowerCase();
  const url = res.config?.url || "";
  if (m !== "get" && !/\/(auth|events|assistant|lens)\b/.test(url)) notify();
  return res;
});

async function poll() {
  if (running || document.hidden || !api.defaults.headers.common.Authorization) return;
  running = true;
  try {
    const { data } = await api.get("/events/since", { params: { after: cursor } });
    const others = (data.events || []).filter((e) => e.client_id !== CLIENT_ID);
    cursor = data.cursor || cursor;
    if (others.length) {
      notify();
      const shown = new Set();
      others.filter((e) => e.label && !e.mine).forEach((e) => {
        const msg = `${e.label}${e.user_name ? ` · ${e.user_name}` : ""}`;
        if (!shown.has(msg)) { shown.add(msg); toast(msg, { duration: 2500 }); }
      });
    }
  } catch (_e) { /* offline / logged out — try again next tick */ }
  running = false;
}

function start() {
  if (timer) return;
  timer = setInterval(poll, 2000);
  document.addEventListener("visibilitychange", () => { if (!document.hidden) poll(); });
  poll();
}

export function useLive(fn) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const cb = () => ref.current && ref.current();
    subs.add(cb);
    start();
    return () => subs.delete(cb);
  }, []);
}
